import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Form,
  Input,
  message,
  Modal,
  Switch,
  Upload,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import { validateNumber } from '../../Validators/ValidatorNumber';
import { internshipIntervalRule } from '../../Validators/internshipIntervalRule';
import {
  GetInternshipOfferById,
  UpdateInternshipOffer,
} from '../../helper/apiHelper';
import type {
  DetailedInternship,
  UpdateInternship,
  UpdateInternshipProps,
} from '../../Types/interfaces';
import { InternshipOfferStatus } from '../../Types/interfaces';
import type { JSX } from 'react';
import { useStudyLines } from '../../hooks/useStudyLines';

const UpdateInternshipComponent = (
  props: UpdateInternshipProps
): JSX.Element => {
  const [form] = Form.useForm();
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const [internship, setInternship] = useState<DetailedInternship>();
  const [isPaid, setIsPaid] = useState<boolean>();
  const [isActive, setIsActive] = useState<boolean>();
  const [messageApi, contextHolder] = message.useMessage();
  const { data: studyLines } = useStudyLines();

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        const response = await GetInternshipOfferById(props.id.toString());
        setInternship(response);

        form.setFieldsValue({
          name: response.name,
          numberOfHours: response.numberOfHours,
          numberOfPositions: response.numberOfPositions,
          paid: response.paid,
          description: response.description,
          sessions: response.internshipSessions,
          city: response.city,
          street: response.street,
          streetNumber: response.streetNumber,
          status: response.status,
          // the date inputs need yyyy-mm-dd
          startTime: String(response.startTime ?? '').slice(0, 10),
          endTime: String(response.endTime ?? '').slice(0, 10),
        });
        if (response.paid) {
          setIsPaid(true);
        }
        if (response.status === InternshipOfferStatus.active) {
          setIsActive(true);
        }
      } catch (error) {
        console.log(error);
        messageApi.error(t('updateInternshipComp.fetchError'));
      }
    };

    fetchData();
  }, [form, props.id, messageApi, t]);

  const showModal = (): void => {
    setOpen(true);
  };

  const handleOk = (): void => {
    form.validateFields().then((values) => {
      const internshipOffer: UpdateInternship = {
        name: values.name,
        numberOfHours: Number(values.numberOfHours),
        numberOfPositions: Number(values.numberOfPositions),
        paid: values.paid,
        logo: ((): string => {
          if (values.logo) {
            return values.logo.file.name;
          }
          return internship ? internship.logo : '';
        })(),
        logoData: ((): string => {
          if (values.logo) {
            return values.logo.fileList[0].thumbUrl.split(',')[1];
          }
          if (internship) {
            return internship.logoData;
          }
          return '';
        })(),
        description: values.description,
        internshipProcessIds: [],
        city: values.city,
        street: values.street,
        streetNumber: values.streetNumber,
        status: values.active
          ? InternshipOfferStatus.active
          : InternshipOfferStatus.inactive,
        startTime: values.startTime,
        endTime: values.endTime,
      };

      UpdateInternshipOffer(props.id.toString(), internshipOffer)
        .then(() => {
          setOpen(false);
          props.onInternShipComponentUpdated();
          messageApi.success(t('updateInternshipComp.success'));
        })
        .catch((error) => {
          messageApi.error(t('updateInternshipComp.error'));
          console.log(error);
        });
    });
  };

  const handleCancel = (): void => {
    setOpen(false);
  };

  const handleBeforeUpload = (): boolean => {
    return false;
  };

  return (
    <>
      {contextHolder}
      <Button onClick={() => showModal()}>
        {t('updateInternshipComp.edit')}
      </Button>
      <Modal
        title={t('updateInternshipComp.editInt')}
        open={open}
        okText={t('updateInternshipComp.submit')}
        cancelText={t('updateInternshipComp.cancel')}
        onOk={handleOk}
        onCancel={handleCancel}
      >
        <Form form={form} size="large">
          <Form.Item name="name" required>
            <Input addonBefore={t('updateInternshipComp.title')} />
          </Form.Item>
          <Form.Item name="description">
            <Input addonBefore={t('updateInternshipComp.desc')} />
          </Form.Item>
          <Form.Item
            name="startTime"
            rules={[
              {
                required: true,
                message: t('newInternshipComp.plsInputStartDate'),
              },
            ]}
          >
            <Input
              type="date"
              addonBefore={t('updateInternshipComp.startDate')}
            ></Input>
          </Form.Item>
          <Form.Item
            name="endTime"
            // checked again when the start date or the hours change
            dependencies={['startTime', 'numberOfHours']}
            // the error lists the study lines without enough hours, one per line
            style={{ whiteSpace: 'pre-line' }}
            rules={[
              {
                required: true,
                message: t('newInternshipComp.plsInputEndDate'),
              },
              internshipIntervalRule(t, {
                start: 'startTime',
                end: 'endTime',
                hours: 'numberOfHours',
                sessions: 'sessions',
              }),
            ]}
          >
            <Input
              type="date"
              addonBefore={t('updateInternshipComp.endDate')}
            ></Input>
          </Form.Item>
          <Form.Item name="sessions">
            <Card title={t('updateInternshipComp.sessions')}>
              {studyLines
                ?.filter((studyLine) =>
                  internship?.internshipSessions.some(
                    (internshipSession) => internshipSession.id === studyLine.id
                  )
                )
                .map((studyLine, index) => {
                  return (
                    <div key={index} style={{ marginBottom: 10 }}>
                      <Card title={studyLine.name}>
                        {studyLine.internshipSessions.map((session) => {
                          return session.description;
                        })}
                      </Card>
                    </div>
                  );
                })}
            </Card>
          </Form.Item>
          <Form.Item
            name="numberOfHours"
            rules={[
              {
                required: true,
                message: t('updateInternshipComp.plsInputDuration'),
              },
              { validator: validateNumber },
            ]}
          >
            <Input addonBefore={t('updateInternshipComp.nrOfHours')} />
          </Form.Item>
          <Form.Item
            name="numberOfPositions"
            rules={[
              {
                required: true,
                message: t('updateInternshipComp.plsInputPositions'),
              },
              { validator: validateNumber },
            ]}
          >
            <Input addonBefore={t('updateInternshipComp.nrOfPositions')} />
          </Form.Item>
          <Form.Item name="paid" required>
            <Switch
              defaultChecked={isPaid}
              checkedChildren={t('newInternshipComp.paid')}
              unCheckedChildren={t('newInternshipComp.unpaid')}
            />
          </Form.Item>
          <Form.Item
            name="city"
            rules={[
              { required: true, message: t('newInternshipComp.plsInputCity') },
            ]}
          >
            <Input addonBefore={t('newInternshipComp.city')} />
          </Form.Item>
          <Form.Item
            name="street"
            rules={[
              {
                required: true,
                message: t('newInternshipComp.plsInputStreet'),
              },
            ]}
          >
            <Input addonBefore={t('newInternshipComp.street')} />
          </Form.Item>
          <Form.Item
            name="streetNumber"
            rules={[
              { required: true, message: t('newInternshipComp.plsInputStrNr') },
            ]}
          >
            <Input addonBefore={t('newInternshipComp.strNumber')} />
          </Form.Item>
          <Form.Item name="active" required>
            <Switch
              defaultChecked={isActive}
              checkedChildren={t('updateInternshipComp.active')}
              unCheckedChildren={t('updateInternshipComp.inactive')}
            />
          </Form.Item>
          {internship?.logoData && (
            <Card style={{ width: 104, height: 104, marginBottom: 16 }}>
              <img
                alt="LOGO"
                src={`data:image/png;base64,${internship.logoData}`}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: '4px',
                }}
              />
            </Card>
          )}
          <Form.Item name="logo">
            <Upload
              accept="image/png, image/jpeg"
              beforeUpload={handleBeforeUpload}
              listType="picture-card"
              maxCount={1}
            >
              <div>
                <PlusOutlined />
                <div style={{ marginTop: 8 }}>
                  {t('newInternshipComp.uploadLogo')}
                </div>
              </div>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default UpdateInternshipComponent;
